import React, { useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "store";
import {
  loadMySubscription,
  setReconciling,
} from "store/slices/subscriptionSlice";
import { fetchUserInfo } from "store/slices/userSlice";
import {
  createSubscription,
  confirmSubscription,
  initBillingMethodChange,
  cancelMySubscription,
  resumeMySubscription,
} from "api/subscriptionService";
import { requestSubscriptionPay } from "utils/subscriptionPayment";
import { SubscriptionPlanCode } from "common/models/types";
import { SUBSCRIPTION_TERMS_VERSION } from "common/constants";
import { SlideManager } from "components/organisms/CommonUI/SlidePopup/SlideManager";
import { ToastManager } from "components/organisms/CommonUI/ToastPopup/ToastManager";
import { ConfirmManager } from "components/organisms/CommonUI/ConfirmPopup/ConfirmManager";
import PremiumSubscribeSlide from "components/templates/MyPageTemplate/Subscription/PremiumSubscribeSlide";
import { Button } from "components/atoms";
import { eventLog } from "utils/AmplitudeUtil";
import { formatKstDate } from "utils/subscriptionDisplay";
import {
  CancelConfirmBody,
  ResumeConfirmBody,
} from "components/templates/MyPageTemplate/Subscription/SubscriptionConfirmBody";

/**
 * 구독 관련 사용자 액션 (가입/카드변경/해지/재개/새로고침)을 한 곳에 모은 훅.
 *
 * - Orders/Points 페이지에서 공유 (단일 책임이 아닌 도메인 응집)
 * - 가입 흐름: createSubscription → requestSubscriptionPay → confirmSubscription
 *   (ymyd-frontend USER_PREMIUM_PURCHASED_POST 패턴 — webhook/reconcile 의존성 제거)
 * - confirm 실패 시: setReconciling(true) 폴백 → 기존 reconcile 폴링이 흡수
 */
export const useSubscriptionActions = () => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((s: RootState) => s.user.user);
  const subscription = useSelector((s: RootState) => s.subscription.me);
  const [isProcessing, setIsProcessing] = useState(false);
  /**
   * 연타(더블파이어) 방지용 동기 가드.
   * isProcessing(state)은 setState→리렌더가 비동기라, 같은 tick 내 빠른 더블클릭은
   * 클로저가 캡처한 stale isProcessing(false)를 통과한다. ref는 즉시 반영되어 2번째
   * 호출을 동기적으로 차단한다. (백엔드 멱등화와 별개로 클라에서 더블파이어 자체 차단)
   */
  const inFlightRef = useRef(false);

  const handleSubscribe = useCallback(
    async (plan: SubscriptionPlanCode) => {
      if (inFlightRef.current) return;
      if (!user || !user.id) {
        ToastManager.show({ content: "로그인이 필요합니다", duration: 3000 });
        return;
      }
      inFlightRef.current = true;
      setIsProcessing(true);
      try {
        // 1. PENDING subscription 생성 + customer_uid 발급
        const createRes = await createSubscription(plan, SUBSCRIPTION_TERMS_VERSION);
        if (createRes.error === 1006) {
          ToastManager.show({
            content: createRes.message || "이미 활성 구독이 있습니다",
            duration: 3000,
          });
          dispatch(loadMySubscription());
          SlideManager.hide();
          return;
        }
        if (createRes.error || !createRes.data) {
          ToastManager.show({
            content: createRes.message || "가입 신청 실패",
            duration: 3000,
          });
          return;
        }

        // 2. PortOne 결제창 — 콜백에서 imp_uid 받음
        const payRes = await requestSubscriptionPay(createRes.data, "register", {
          email: user.email,
          name: user.name,
          phone: user.phone,
        });
        if (!payRes.success || !payRes.impUid || !payRes.merchantUid) {
          eventLog("유료회원_가입실패", {
            planCode: plan,
            errorMsg: payRes.errorMsg,
            errorCode: payRes.errorCode,
          });
          if (payRes.errorMsg) {
            ToastManager.show({
              content: `결제 실패: ${payRes.errorMsg}`,
              duration: 3000,
            });
          }
          return;
        }

        // 3. 백엔드 확정 호출 (ymyd-backend USER_PREMIUM_PURCHASED 패턴)
        const confirmRes = await confirmSubscription({
          impUid: payRes.impUid,
          merchantUid: payRes.merchantUid,
        });

        if (confirmRes.error) {
          // confirm 실패 → reconcile 폴링 폴백 (데이터 손실 없음)
          eventLog("유료회원_확정실패_폴링대기", {
            planCode: plan,
            errorMsg: confirmRes.message,
            impUid: payRes.impUid,
          });
          dispatch(setReconciling(true));
          SlideManager.hide();
          ToastManager.show({
            content: "결제는 완료됐습니다. 백엔드 확인 중 (최대 30초)",
            duration: 5000,
          });
          return;
        }

        // 4. 성공 — 즉시 ACTIVE
        eventLog("유료회원_가입성공", {
          planCode: plan,
          amount: createRes.data.amount,
          subscriptionId: createRes.data.subscriptionId,
        });
        dispatch(loadMySubscription());
        // user.isPremium(=isPremiumUser/포인트 무차감 게이팅)도 갱신 — 구독 슬라이스만으론 전역 프리미엄 표시가 stale
        dispatch(fetchUserInfo());
        SlideManager.hide();
        ToastManager.show({
          content: "정기결제가 시작되었습니다",
          duration: 3000,
        });
      } finally {
        inFlightRef.current = false;
        setIsProcessing(false);
      }
    },
    [dispatch, user]
  );

  const openSubscribeSlide = useCallback(() => {
    SlideManager.show({
      body: React.createElement(PremiumSubscribeSlide, {
        isProcessing,
        onSubscribe: handleSubscribe,
      }),
      direction: "right",
      width: "500px",
      height: "100vh",
      needCloseButton: false,
      padding: "0px",
    });
  }, [handleSubscribe, isProcessing]);

  const handleChangeCard = useCallback(async () => {
    if (inFlightRef.current) return;
    if (!user || !user.id) {
      ToastManager.show({ content: "로그인이 필요합니다", duration: 3000 });
      return;
    }
    inFlightRef.current = true;
    setIsProcessing(true);
    try {
      const res = await initBillingMethodChange();
      if (res.error || !res.data) {
        ToastManager.show({
          content: res.message || "카드 변경을 시작할 수 없습니다",
          duration: 3000,
        });
        return;
      }
      const payRes = await requestSubscriptionPay(res.data, "change", {
        email: user.email,
        name: user.name,
        phone: user.phone,
      });
      // 카드 변경은 confirm 미호출 (계획 3.4 A-1) — webhook이 카드 정보 갱신
      if (payRes.success) {
        eventLog("유료회원_카드변경성공", {});
        ToastManager.show({ content: "카드가 변경되었습니다", duration: 2000 });
        dispatch(loadMySubscription());
      } else if (payRes.errorMsg) {
        eventLog("유료회원_카드변경실패", {
          errorMsg: payRes.errorMsg,
          errorCode: payRes.errorCode,
        });
        ToastManager.show({
          content: `변경 실패: ${payRes.errorMsg}`,
          duration: 3000,
        });
      }
    } finally {
      inFlightRef.current = false;
      setIsProcessing(false);
    }
  }, [dispatch, user]);

  const handleCancel = useCallback(() => {
    if (inFlightRef.current) return;
    const periodEnd = subscription?.currentPeriodEnd
      ? formatKstDate(subscription.currentPeriodEnd)
      : null;
    ConfirmManager.show({
      title: "유료 프리미엄 해지",
      body: React.createElement(CancelConfirmBody, { periodEnd }),
      buttons: [
        React.createElement(
          Button,
          {
            key: "cancel",
            size: "Medium",
            type: "Text",
            variant: "Outlined",
            color: "Secondary",
            onClick: () => ConfirmManager.hide(),
          },
          "취소"
        ),
        React.createElement(
          Button,
          {
            key: "confirm",
            size: "Medium",
            type: "Text",
            variant: "Contained",
            color: "Negative",
            onClick: async () => {
              if (inFlightRef.current) return;
              inFlightRef.current = true;
              ConfirmManager.hide();
              setIsProcessing(true);
              try {
                const res = await cancelMySubscription();
                if (res.error) {
                  ToastManager.show({
                    content: res.message || "해지 실패",
                    duration: 3000,
                  });
                  return;
                }
                eventLog("유료회원_해지성공", {});
                ToastManager.show({
                  content: "구독이 해지되었습니다",
                  duration: 2000,
                });
                dispatch(loadMySubscription());
              } finally {
                inFlightRef.current = false;
                setIsProcessing(false);
              }
            },
          },
          "해지하기"
        ),
      ],
    });
  }, [dispatch, subscription]);

  const handleResume = useCallback(() => {
    if (inFlightRef.current) return;
    const periodEnd = subscription?.currentPeriodEnd
      ? formatKstDate(subscription.currentPeriodEnd)
      : null;
    ConfirmManager.show({
      title: "멤버십 재개",
      body: React.createElement(ResumeConfirmBody, { periodEnd }),
      buttons: [
        React.createElement(
          Button,
          {
            key: "cancel",
            size: "Medium",
            type: "Text",
            variant: "Outlined",
            color: "Secondary",
            onClick: () => ConfirmManager.hide(),
          },
          "취소"
        ),
        React.createElement(
          Button,
          {
            key: "confirm",
            size: "Medium",
            type: "Text",
            variant: "Contained",
            color: "Primary",
            onClick: async () => {
              if (inFlightRef.current) return;
              inFlightRef.current = true;
              ConfirmManager.hide();
              setIsProcessing(true);
              try {
                const res = await resumeMySubscription();
                if (res.error || !res.data) {
                  ToastManager.show({
                    content: res.message || "재개 실패",
                    duration: 3000,
                  });
                  return;
                }
                eventLog("유료회원_재가입성공", {});
                ToastManager.show({
                  content: "구독이 재개되었습니다",
                  duration: 2000,
                });
                dispatch(loadMySubscription());
                dispatch(fetchUserInfo());
              } finally {
                inFlightRef.current = false;
                setIsProcessing(false);
              }
            },
          },
          "멤버십 재개"
        ),
      ],
    });
  }, [dispatch, subscription]);

  const handleRefresh = useCallback(() => {
    dispatch(loadMySubscription());
  }, [dispatch]);

  return {
    isProcessing,
    openSubscribeSlide,
    handleChangeCard,
    handleCancel,
    handleResume,
    handleRefresh,
  };
};
